"use client";

import { useState, type ReactNode } from "react";
import { BookOpenIcon, ExternalLinkIcon, GlobeIcon } from "lucide-react";
import { formatPartitionLabel } from "@/agents-v2/utils/formatPartitionLabel";
import {
  asRecord,
  getFirstStringValue,
  getStringValue,
  parseMaybeJson
} from "@/agents-v2/utils/unknown";

const CONTENT_LIBRARY_TOOL = "content_library_search";
const WEB_SEARCH_TOOL = "external_research_search";
const URL_RE = /https?:\/\/[^\s<>)\]]+/g;

type RetrievalToolPart = {
  toolName: string;
  result?: unknown;
};

type RetrievalSource = {
  id: string;
  type: "library" | "web";
  title: string;
  subtitle?: string;
  preview?: string;
  /** All retrieved chunk previews for this document (multiple rows collapse). */
  previews?: string[];
  url?: string;
};

const getSafeHttpUrl = (value: string | null): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
};

const getSafeContentLibraryUrl = (value: string | null): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "mytender-source:" &&
      url.pathname === "content_library"
      ? value
      : null;
  } catch {
    return null;
  }
};

const contentLibrarySourceUrl = (
  filename: string,
  partition?: string
): string => {
  const params = new URLSearchParams({
    filename,
    partition: partition ?? ""
  });
  return `mytender-source:content_library?${params.toString()}`;
};

const openContentLibrarySource = (href: string, snippets?: string[]) => {
  window.dispatchEvent(
    new CustomEvent("agents:open-source", {
      detail: { href, snippets }
    })
  );
};

const extractContentLibrarySources = (result: unknown): RetrievalSource[] => {
  const parsed = parseMaybeJson(result);
  const record = asRecord(parsed);
  const documents = Array.isArray(record.documents)
    ? record.documents
    : Array.isArray(parsed)
      ? parsed
      : [];

  return documents
    .map((document, index): RetrievalSource | null => {
      const documentRecord = asRecord(document);
      const nestedSource = asRecord(documentRecord.source);
      const sourceRecord =
        Object.keys(nestedSource).length > 0 ? nestedSource : documentRecord;
      const title = getFirstStringValue(sourceRecord, [
        "filename",
        "label",
        "source",
        "name",
        "title"
      ]);
      if (!title) return null;

      const partition =
        getStringValue(sourceRecord, "partition") ??
        getStringValue(documentRecord, "partition") ??
        undefined;
      const preview =
        getStringValue(sourceRecord, "content_preview") ??
        getStringValue(documentRecord, "content") ??
        undefined;
      const url =
        getSafeContentLibraryUrl(
          getFirstStringValue(sourceRecord, ["url", "source_url"])
        ) ?? contentLibrarySourceUrl(title, partition);

      return {
        id: `library:${url}:${index}`,
        type: "library",
        title,
        // Never show the raw FORWARDSLASH storage token in the chat UI.
        subtitle: partition ? formatPartitionLabel(partition) : undefined,
        preview,
        url
      };
    })
    .filter((source): source is RetrievalSource => source !== null);
};

const extractWebSources = (result: unknown): RetrievalSource[] => {
  const parsed = parseMaybeJson(result);
  const record = asRecord(parsed);
  const structuredSources = Array.isArray(record.sources)
    ? record.sources
    : Array.isArray(parsed)
      ? parsed
      : [];
  const sources = structuredSources
    .map((source, index): RetrievalSource | null => {
      const sourceRecord = asRecord(source);
      const url = getSafeHttpUrl(getStringValue(sourceRecord, "url"));
      if (!url) return null;
      const title =
        getFirstStringValue(sourceRecord, [
          "label",
          "title",
          "source",
          "name"
        ]) ?? url;
      return {
        id: `web:${url}:${index}`,
        type: "web",
        title,
        subtitle: url,
        url
      };
    })
    .filter((source): source is RetrievalSource => source !== null);

  const text =
    typeof parsed === "string"
      ? parsed
      : (getStringValue(record, "content") ?? "");
  const urls = Array.from(text.matchAll(URL_RE));
  for (const [index, match] of urls.entries()) {
    const url = getSafeHttpUrl(match[0].replace(/[.,;:]+$/, ""));
    if (!url) continue;
    sources.push({
      id: `web:${url}:text:${index}`,
      type: "web",
      title: url,
      subtitle: url,
      url
    });
  }

  return sources;
};

const dedupeRetrievalSources = (
  sources: readonly RetrievalSource[]
): RetrievalSource[] => {
  const seen = new Map<string, RetrievalSource>();
  const order: RetrievalSource[] = [];
  for (const source of sources) {
    const key = `${source.type}:${source.url ?? source.title}:${source.subtitle ?? ""}`;
    const existing = seen.get(key);
    if (existing) {
      // Keep every distinct chunk preview so the source popup can highlight
      // (and navigate between) all passages from the same document.
      if (source.preview && !existing.previews?.includes(source.preview)) {
        existing.previews = [...(existing.previews ?? []), source.preview];
      }
      continue;
    }
    const copy: RetrievalSource = {
      ...source,
      previews: source.preview ? [source.preview] : []
    };
    seen.set(key, copy);
    order.push(copy);
  }
  return order;
};

export const getRetrievalSources = (
  parts: readonly RetrievalToolPart[]
): RetrievalSource[] => {
  return dedupeRetrievalSources(
    parts.flatMap((part) => {
      if (part.toolName === CONTENT_LIBRARY_TOOL) {
        return extractContentLibrarySources(part.result);
      }
      if (part.toolName === WEB_SEARCH_TOOL) {
        return extractWebSources(part.result);
      }
      return [];
    })
  );
};

export function RetrievalSourcesDropdown({
  sources,
  children
}: {
  sources: readonly RetrievalSource[];
  children: (props: {
    open: boolean;
    sourceCount: number;
    toggleOpen: () => void;
  }) => ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div data-slot="retrieval-sources-dropdown" className="w-full">
      {children({
        open,
        sourceCount: sources.length,
        toggleOpen: () => setOpen((current) => !current)
      })}
      {open ? (
        <div className="w-full">
          {sources.map((source) => (
            <RetrievalSourceRow key={source.id} source={source} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RetrievalSourceRow({ source }: { source: RetrievalSource }) {
  const Icon = source.type === "web" ? GlobeIcon : BookOpenIcon;
  const content = (
    <>
      <Icon className="size-4 shrink-0 text-muted-foreground/70" />
      <span className="min-w-0 flex-1 truncate">
        <span className="truncate">{source.title}</span>
        {source.subtitle ? (
          <span className="text-muted-foreground/60"> · {source.subtitle}</span>
        ) : null}
      </span>
      {source.type === "web" ? (
        <ExternalLinkIcon className="size-4 shrink-0 text-muted-foreground/60" />
      ) : null}
    </>
  );

  if (source.type === "web" && source.url) {
    return (
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        className="my-2 flex w-full items-center gap-2 rounded-md px-1 py-1 text-sm text-muted-foreground"
      >
        {content}
      </a>
    );
  }

  // Library sources open the full document directly, with the retrieved chunk
  // highlighted (the preview text is passed as the snippet to highlight).
  return (
    <button
      type="button"
      className="my-2 flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-sm text-muted-foreground"
      onClick={() =>
        source.url &&
        openContentLibrarySource(
          source.url,
          source.previews?.length
            ? source.previews
            : source.preview
              ? [source.preview]
              : undefined
        )
      }
    >
      {content}
    </button>
  );
}
